import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Hourglass,
  Phone,
  Mail,
  MapPin,
  Clock,
  Wallet,
  Search,
  Trash2,
} from "lucide-react";
import { api } from "../lib/api";

const STATUS_OPTIONS = [
  { value: "pending", label: "In attesa" },
  { value: "confirmed", label: "Confermati" },
  { value: "rejected", label: "Rifiutati" },
  { value: "", label: "Tutti" },
];

const STATUS_META = {
  pending: { label: "In attesa", icon: Hourglass, color: "text-slate1", bg: "bg-cream2 border-ink/15" },
  confirmed: { label: "Confermato", icon: CheckCircle2, color: "text-gold", bg: "bg-gold/10 border-gold/30" },
  rejected: { label: "Rifiutato", icon: XCircle, color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase rounded-sm border ${m.bg} ${m.color}`}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/contacts${params}`);
      setContacts(data);
    } catch {
      toast.error("Errore caricamento contatti");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!q) return contacts;
    const k = q.toLowerCase();
    return contacts.filter(
      (c) =>
        (c.first_name + " " + c.last_name).toLowerCase().includes(k) ||
        (c.email || "").toLowerCase().includes(k) ||
        (c.user_email || "").toLowerCase().includes(k) ||
        (c.agency || "").toLowerCase().includes(k) ||
        (c.property_address || "").toLowerCase().includes(k)
    );
  }, [contacts, q]);

  async function confirmContact(c) {
    if (!window.confirm(`Confermare il contatto ${c.first_name} ${c.last_name}? Verranno accreditati €${c.credit_amount} sul wallet di ${c.user_email}.`)) return;
    setActingId(c.id);
    try {
      await api.put(`/admin/contacts/${c.id}/confirm`);
      toast.success(`Contatto confermato. €${c.credit_amount} accreditati a ${c.user_email}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore conferma");
    } finally {
      setActingId(null);
    }
  }

  async function rejectContact(c) {
    const reason = window.prompt(`Motivo del rifiuto (opzionale):`, "");
    if (reason === null) return; // cancelled
    setActingId(c.id);
    try {
      await api.put(`/admin/contacts/${c.id}/reject`, { reason });
      toast.success("Contatto rifiutato");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setActingId(null);
    }
  }

  async function deleteContact(c) {
    const warn = c.status === "confirmed"
      ? `ATTENZIONE: questo contatto era già CONFERMATO ed erano stati accreditati €${c.credit_amount} sul wallet di ${c.user_email}.\n\nEliminandolo il credito NON verrà stornato automaticamente (rimane nell'audit). Procedere comunque?`
      : `Eliminare definitivamente il contatto di ${c.first_name} ${c.last_name}?\n\nL'operazione non può essere annullata.`;
    if (!window.confirm(warn)) return;
    setActingId(c.id);
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      toast.success("Contatto eliminato");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    } finally {
      setActingId(null);
    }
  }

  return (
    <div data-testid="admin-contacts">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">Real Estate</div>
          <h2 className="mt-1 font-display text-2xl text-ink">Contatti qualificati</h2>
          <p className="text-xs text-slate1 mt-1">Conferma i contatti che hanno effettivamente prenotato un preventivo per accreditare i €100.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="inline-flex border border-ink/20 rounded-sm overflow-hidden">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value || "all"}
                onClick={() => setStatusFilter(o.value)}
                className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase ${statusFilter === o.value ? "bg-ink text-cream" : "bg-white text-slate1 hover:bg-cream2"}`}
                data-testid={`admin-contacts-filter-${o.value || "all"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca cliente, agenzia…"
              className="w-full pl-9 pr-3 py-2 border border-ink/20 bg-white rounded-sm text-sm"
              data-testid="admin-contacts-search"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate1">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-ink/20 rounded-sm p-10 text-center text-sm text-slate1" data-testid="admin-contacts-empty">
          Nessun contatto da mostrare.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white border border-ink/10 rounded-sm p-5 grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`admin-contact-${c.id}`}>
              <div className="lg:col-span-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Cliente</div>
                <div className="mt-1 font-display text-lg text-ink">{c.first_name} {c.last_name}</div>
                <div className="text-xs text-slate1 mt-1 inline-flex items-center gap-1.5"><Mail size={11} /> {c.email}</div>
                <div className="text-xs text-slate1 inline-flex items-center gap-1.5"><Phone size={11} /> {c.phone}</div>
              </div>
              <div className="lg:col-span-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Caricato da</div>
                <div className="mt-1 text-sm text-ink">{c.user_name || c.user_email}</div>
                {c.agency && <div className="text-xs text-slate1">{c.agency}</div>}
                <div className="text-[11px] text-slate1 mt-1">{new Date(c.created_at).toLocaleString("it-IT")}</div>
              </div>
              <div className="lg:col-span-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5"><MapPin size={11} /> Immobile</div>
                <div className="text-sm text-ink mt-1">{c.property_address}</div>
                <div className="text-xs text-slate1 mt-1 inline-flex items-center gap-1.5"><Clock size={11} /> {c.estimated_time_label}</div>
                {c.notes && <div className="text-[11px] text-slate1 italic mt-1">&ldquo;{c.notes}&rdquo;</div>}
              </div>
              <div className="lg:col-span-3 flex flex-col gap-3 lg:items-end">
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gold/10 text-gold text-[10px] tracking-[0.2em] uppercase rounded-sm">
                    <Wallet size={11} /> €{c.credit_amount}
                  </span>
                </div>
                {c.status === "pending" && (
                  <div className="flex gap-2 w-full lg:w-auto">
                    <button
                      onClick={() => confirmContact(c)}
                      disabled={actingId === c.id}
                      className="flex-1 lg:flex-none inline-flex items-center justify-center gap-1.5 bg-ink text-cream px-3 py-2 text-[10px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50"
                      data-testid={`admin-contact-confirm-${c.id}`}
                    >
                      <CheckCircle2 size={11} /> Conferma
                    </button>
                    <button
                      onClick={() => rejectContact(c)}
                      disabled={actingId === c.id}
                      className="flex-1 lg:flex-none inline-flex items-center justify-center gap-1.5 border border-ink/20 px-3 py-2 text-[10px] tracking-[0.2em] uppercase rounded-sm text-slate1 hover:border-rose-300 hover:text-rose-700 disabled:opacity-50"
                      data-testid={`admin-contact-reject-${c.id}`}
                    >
                      <XCircle size={11} /> Rifiuta
                    </button>
                  </div>
                )}
                {c.status === "confirmed" && (
                  <div className="text-[11px] text-slate1">
                    Confermato {c.confirmed_at ? new Date(c.confirmed_at).toLocaleDateString("it-IT") : "—"}
                    {c.confirmed_by && <> da <span className="text-ink">{c.confirmed_by}</span></>}
                  </div>
                )}
                {c.status === "rejected" && c.rejection_reason && (
                  <div className="text-[11px] text-rose-700 italic">&ldquo;{c.rejection_reason}&rdquo;</div>
                )}
                <button
                  onClick={() => deleteContact(c)}
                  disabled={actingId === c.id}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-slate1 hover:text-rose-700 disabled:opacity-50 mt-1"
                  data-testid={`admin-contact-delete-${c.id}`}
                  title="Elimina definitivamente questo contatto"
                >
                  <Trash2 size={11} /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
